// Art groups. The collection identity axis that sits between "card" and
// "printing".
//
// A card's printings are partitioned by ILLUSTRATION. Nearly every card has
// one group: the booster art, reprinted verbatim across sets and finishes.
// Around 45 cards carry a second illustration on a promo, box-topper or
// precon printing (and four Alpha cards were re-illustrated for Beta). Those
// count as their own card in the deck builder. Their own tile, their own
// foil, their own owned count. But NOT in deckbuilding rules: an Ordinary
// card is still capped at four copies across every art and finish it has.
//
// The default group is the literal string 'base' rather than the card's own
// booster slug, so the axis is a no-op everywhere that doesn't care: existing
// collection rows, pack grants and deck entries all sit at 'base' with no
// migration and no lookup.
//
// The alt-art membership map is generated from the card images. See
// scripts/build-art-groups.js for why the product code and artist field both
// misclassify it.

import { ALT_ART_GROUP_BY_SLUG_BASE, ALT_ART_GROUP_META } from './artGroups.data';

export const ART_GROUP_BASE = 'base';

// Printed titles on alt-art printings that differ from the card's real name
// (the Welcome Kit parody wizards). No upstream data source carries these.
// the catalog stores one name per card. So they are hand-authored, keyed by
// art group. Rules identity is unchanged: the card IS Apprentice Wizard; only
// the displayed title follows the printed art.
const ALT_ART_TITLES = {
  'pro-apprentice_wizard-wk': 'Apurrentice Wizard',
  'pro-grandmaster_wizard-wk': 'Grandmeowster Wizard',
};

const PRODUCT_LABELS = {
  Welcome_Kit: 'Welcome Kit',
  Organized_Play: 'Organized Play',
  Alpha_Investments: 'Alpha Investments',
  Team_Covenant: 'Team Covenant',
  Star_City_Games: 'Star City Games',
  Preconstructed_Deck: 'Precon',
  Draft_Kit: 'Draft Kit',
  Box_Topper: 'Box Topper',
  Kickstarter: 'Kickstarter',
  Dust: 'Dust',
};

// A variant slug minus its finish suffix. The standard and foil printings of
// one illustration share a base, which is why the generated map is keyed by
// base rather than by slug.
export function artworkBase(slug) {
  return String(slug || '').replace(/-(s|f|rf)$/, '');
}

// Art group of a single printing. Accepts a printing object or a bare slug.
export function artGroupOf(printing) {
  const slug = typeof printing === 'string' ? printing : printing?.unique_id;
  return ALT_ART_GROUP_BY_SLUG_BASE[artworkBase(slug)] || ART_GROUP_BASE;
}

// Every art group the card has a printing for, base first, then alt groups in
// catalog order. Cards without an alt printing return just ['base'].
export function artGroupsForCard(card) {
  const groups = [ART_GROUP_BASE];
  for (const printing of card?.printings || []) {
    const group = artGroupOf(printing);
    if (group !== ART_GROUP_BASE && !groups.includes(group)) groups.push(group);
  }
  return groups;
}

// The card's printings that belong to `group`, optionally narrowed to one
// foiling. Order follows the catalog, so [0] is the earliest printing.
export function printingsInArtGroup(card, group, foiling = null) {
  return (card?.printings || []).filter((p) =>
    artGroupOf(p) === group && (foiling === null || (p.foiling || 'S') === foiling)
  );
}

// Human label distinguishing an alt group from its card's base art, e.g.
// 'Welcome Kit', 'Beta', 'Gothic Box Topper'. Empty for the base group.
// base tiles show the plain card name with no qualifier.
//
// Promotional printings are named by their product (the set is always
// 'Promotional', which says nothing); everything else leads with the set,
// because two alt groups of one card can share a product. Rubble has both an
// Arthurian and a Gothic box topper.
export function artGroupLabel(group) {
  const meta = ALT_ART_GROUP_META[group];
  if (!meta) return '';
  const product = PRODUCT_LABELS[meta.product] || '';
  if (meta.set === 'Promotional') return product;
  return product ? `${meta.set} ${product}` : meta.set;
}

// Display title for an art group: the printed alt-art title when the art
// carries one, else the card's real name.
export function artGroupDisplayName(card, group) {
  return ALT_ART_TITLES[group] || card?.name || '';
}
